import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUserRole } from '@/hooks/useUserRole';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { RefreshCw, Search, X, Bot, User, Server, ShieldAlert } from 'lucide-react';

interface AuditLog {
  id: string;
  organization_id: string | null;
  actor_type: 'user' | 'mcp' | 'system';
  actor_id: string | null;
  actor_label: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  status: 'success' | 'error';
  error_message: string | null;
  source: 'db_trigger' | 'mcp' | 'app';
  metadata: Record<string, unknown>;
  created_at: string;
}

type ActorFilter = 'all' | 'user' | 'mcp' | 'system';
type StatusFilter = 'all' | 'success' | 'error';
type SourceFilter = 'all' | 'app' | 'mcp' | 'db_trigger';
type DaysFilter = '1' | '7' | '30' | 'all';

const ACTOR_LABEL: Record<AuditLog['actor_type'], string> = {
  user: 'Utilisateur',
  mcp: 'MCP',
  system: 'Système',
};

const SOURCE_LABEL: Record<AuditLog['source'], string> = {
  app: 'Application',
  mcp: 'MCP',
  db_trigger: 'Base de données',
};

function ActorBadge({ log }: { log: AuditLog }) {
  const Icon = log.actor_type === 'mcp' ? Bot : log.actor_type === 'user' ? User : Server;
  const variant = log.actor_type === 'mcp' ? 'default' : log.actor_type === 'user' ? 'secondary' : 'outline';
  return (
    <Badge variant={variant} className="gap-1 font-medium">
      <Icon className="h-3 w-3" />
      {ACTOR_LABEL[log.actor_type]}
    </Badge>
  );
}

export default function Logs() {
  const { isAdmin } = useUserRole();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [actorType, setActorType] = useState<ActorFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [days, setDays] = useState<DaysFilter>('7');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const { data: logs = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['audit-logs', { search, actorType, status, source, days }],
    queryFn: async () => {
      // audit_logs is not in the generated types yet; access it untyped.
      let q = (supabase as any)
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(300);

      if (actorType !== 'all') q = q.eq('actor_type', actorType);
      if (status !== 'all') q = q.eq('status', status);
      if (source !== 'all') q = q.eq('source', source);
      if (days !== 'all') {
        const since = new Date(Date.now() - Number(days) * 86400_000).toISOString();
        q = q.gte('created_at', since);
      }
      const term = search.trim();
      if (term) q = q.or(`action.ilike.%${term}%,resource_id.ilike.%${term}%,resource_type.ilike.%${term}%`);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as AuditLog[];
    },
    enabled: isAdmin,
  });

  // Live refresh so a misbehaving MCP is visible as it happens.
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel('audit-logs-stream')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'audit_logs' }, () => {
        queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, queryClient]);

  const hasFilters = search !== '' || actorType !== 'all' || status !== 'all' || source !== 'all' || days !== '7';
  const clearFilters = () => {
    setSearch('');
    setActorType('all');
    setStatus('all');
    setSource('all');
    setDays('7');
  };

  if (!isAdmin) {
    return (
      <AppLayout title="Journaux d'audit" description="Trace des actions de l'application et du MCP">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <ShieldAlert className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Accès réservé aux administrateurs</p>
          <p className="text-sm text-muted-foreground">
            Les journaux d'audit ne sont visibles que par les administrateurs de l'organisation.
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Journaux d'audit"
      description="Trace complète des actions — application et MCP"
      actions={
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher une action, un type ou un identifiant…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select value={actorType} onValueChange={(v) => setActorType(v as ActorFilter)}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les acteurs</SelectItem>
              <SelectItem value="mcp">MCP</SelectItem>
              <SelectItem value="user">Utilisateur</SelectItem>
              <SelectItem value="system">Système</SelectItem>
            </SelectContent>
          </Select>

          <Select value={source} onValueChange={(v) => setSource(v as SourceFilter)}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les sources</SelectItem>
              <SelectItem value="mcp">MCP</SelectItem>
              <SelectItem value="app">Application</SelectItem>
              <SelectItem value="db_trigger">Base de données</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="success">Succès</SelectItem>
              <SelectItem value="error">Erreur</SelectItem>
            </SelectContent>
          </Select>

          <Select value={days} onValueChange={(v) => setDays(v as DaysFilter)}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Dernières 24h</SelectItem>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="mr-1 h-4 w-4" />
              Réinitialiser
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[170px]">Date</TableHead>
                <TableHead className="w-[130px]">Acteur</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Ressource</TableHead>
                <TableHead className="w-[110px]">Statut</TableHead>
                <TableHead className="w-[140px]">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    Chargement…
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    Aucun journal pour ces critères.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(log)}
                  >
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {format(new Date(log.created_at), 'dd MMM yyyy HH:mm:ss', { locale: fr })}
                    </TableCell>
                    <TableCell><ActorBadge log={log} /></TableCell>
                    <TableCell className="font-mono text-sm">{log.action}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.resource_type ? (
                        <span>
                          {log.resource_type}
                          {log.resource_id && (
                            <span className="ml-1 font-mono text-xs opacity-70">
                              {log.resource_id.slice(0, 8)}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={log.status === 'error' ? 'destructive' : 'outline'}>
                        {log.status === 'error' ? 'Erreur' : 'Succès'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {SOURCE_LABEL[log.source]}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {logs.length >= 300 && (
          <p className="text-center text-xs text-muted-foreground">
            300 entrées les plus récentes affichées. Affinez les filtres pour voir davantage.
          </p>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-xl">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-mono text-base">
                  {selected.action}
                  <Badge variant={selected.status === 'error' ? 'destructive' : 'outline'}>
                    {selected.status === 'error' ? 'Erreur' : 'Succès'}
                  </Badge>
                </SheetTitle>
                <SheetDescription>
                  {format(new Date(selected.created_at), "dd MMMM yyyy 'à' HH:mm:ss", { locale: fr })}
                </SheetDescription>
              </SheetHeader>

              <ScrollArea className="mt-4 h-[calc(100vh-9rem)] pr-4">
                <div className="space-y-4 text-sm">
                  <dl className="grid grid-cols-3 gap-x-3 gap-y-2">
                    <dt className="text-muted-foreground">Acteur</dt>
                    <dd className="col-span-2">
                      {selected.actor_label ?? ACTOR_LABEL[selected.actor_type]}
                      {selected.actor_id && (
                        <span className="ml-1 font-mono text-xs opacity-60">{selected.actor_id}</span>
                      )}
                    </dd>

                    <dt className="text-muted-foreground">Source</dt>
                    <dd className="col-span-2">{SOURCE_LABEL[selected.source]}</dd>

                    <dt className="text-muted-foreground">Ressource</dt>
                    <dd className="col-span-2">
                      {selected.resource_type ?? '—'}
                      {selected.resource_id && (
                        <span className="ml-1 font-mono text-xs opacity-60">{selected.resource_id}</span>
                      )}
                    </dd>

                    {selected.organization_id && (
                      <>
                        <dt className="text-muted-foreground">Organisation</dt>
                        <dd className="col-span-2 font-mono text-xs">{selected.organization_id}</dd>
                      </>
                    )}
                  </dl>

                  {selected.error_message && (
                    <div>
                      <p className="mb-1 font-medium text-destructive">Message d'erreur</p>
                      <pre className="overflow-x-auto rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                        {selected.error_message}
                      </pre>
                    </div>
                  )}

                  <div>
                    <p className="mb-1 font-medium">Détails</p>
                    <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">
                      {JSON.stringify(selected.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
